/**
 * useValidation Hook
 * Handles validation of user records
 */
import { useState, useCallback, useMemo } from "react";
import { useCspApi } from "@perfectserve/http-ps";
import { createValidationService } from "../services/validationService";
/**
 * Hook for validating user records
 */
export function useValidation() {
    const { get, put } = useCspApi();
    const [isValidating, setIsValidating] = useState(false);
    const [currentRow, setCurrentRow] = useState(0);
    const [totalRows, setTotalRows] = useState(0);
    const [errors, setErrors] = useState([]);
    // Create validation service with HTTP functions
    const validationService = useMemo(() => createValidationService(get, put), [get, put]);
    const validateRecords = useCallback(async (records) => {
        setIsValidating(true);
        setCurrentRow(0);
        setTotalRows(records.length);
        setErrors([]);
        // Clear previous validation errors from records
        for (const record of records) {
            record.validationErrors = [];
        }
        try {
            const allErrors = await validationService.validateAllRecords(records, (current, total) => {
                setCurrentRow(current);
                setTotalRows(total);
            });
            setErrors(allErrors);
            return allErrors;
        }
        finally {
            setIsValidating(false);
        }
    }, [validationService]);
    const clear = useCallback(() => {
        setIsValidating(false);
        setCurrentRow(0);
        setTotalRows(0);
        setErrors([]);
        validationService.clearCache();
    }, [validationService]);
    const isValid = errors.length === 0 && !isValidating && totalRows > 0;
    return {
        isValidating,
        currentRow,
        totalRows,
        errors,
        isValid,
        validateRecords,
        clear,
    };
}
